import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { toast } from 'sonner';
import { Users, UserCheck, UserX, Search, Shield, X } from 'lucide-react';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [showInactiveNameModal, setShowInactiveNameModal] = useState(false);
  const [inactiveNameInput, setInactiveNameInput] = useState('');
  const [userToDeactivate, setUserToDeactivate] = useState(null);
  const [checkingName, setCheckingName] = useState(false);

  useEffect(() => {
    loadCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      loadUsers();
    }
  }, [currentUser]);

  const loadCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: userData } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();

      setCurrentUser(userData);

      // Check if user is admin
      if (userData?.role !== 'admin') {
        toast.error('Access denied. Admin privileges required.');
      }
    } catch (error) {
      console.error('Error loading current user:', error);
      toast.error('Failed to load user information');
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;

      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    // Prevent admin from deactivating themselves
    if (userId === currentUser?.id) {
      toast.error('You cannot deactivate your own account');
      return;
    }

    const newStatus = !currentStatus;

    // If deactivating (newStatus = false)
    if (!newStatus) {
      const user = users.find(u => u.id === userId);
      
      // If user already has an inactive_user_name, just deactivate without popup
      if (user.inactive_user_name) {
        await performDeactivation(userId, user.inactive_user_name);
      } else {
        // Show popup to get inactive name
        setUserToDeactivate(user);
        setInactiveNameInput('');
        setShowInactiveNameModal(true);
      }
    } else {
      // Reactivating - clear inactive_user_name
      await performActivation(userId);
    }
  };

  const performDeactivation = async (userId, inactiveName) => {
    try {
      setUpdating(userId);

      const { data, error } = await supabase
        .from('users')
        .update({ 
          is_active: false,
          inactive_user_name: inactiveName
        })
        .eq('id', userId)
        .select();

      if (error) {
        console.error('Update error:', error);
        toast.error(`Failed to update: ${error.message}`);
        return;
      }

      console.log('Deactivation successful:', data);
      toast.success('User deactivated successfully');
      loadUsers();
    } catch (error) {
      console.error('Error deactivating user:', error);
      toast.error('Failed to deactivate user: ' + error.message);
    } finally {
      setUpdating(null);
    }
  };

  const performActivation = async (userId) => {
    try {
      setUpdating(userId);

      const { data, error } = await supabase
        .from('users')
        .update({ 
          is_active: true,
          inactive_user_name: null
        })
        .eq('id', userId)
        .select();

      if (error) {
        console.error('Update error:', error);
        toast.error(`Failed to update: ${error.message}`);
        return;
      }

      console.log('Activation successful:', data);
      toast.success('User activated successfully');
      loadUsers();
    } catch (error) {
      console.error('Error activating user:', error);
      toast.error('Failed to activate user: ' + error.message);
    } finally {
      setUpdating(null);
    }
  };

  const checkInactiveNameExists = async (name) => {
    if (!name.trim()) return false;

    try {
      const { data, error } = await supabase
        .from('users')
        .select('id')
        .eq('inactive_user_name', name.trim())
        .limit(1);

      if (error) {
        console.error('Error checking name:', error);
        return false;
      }

      return data && data.length > 0;
    } catch (error) {
      console.error('Error checking inactive name:', error);
      return false;
    }
  };

  const handleInactiveNameSubmit = async () => {
    if (!inactiveNameInput.trim()) {
      toast.error('Please enter an inactive user name');
      return;
    }

    // Check if name already exists
    setCheckingName(true);
    const exists = await checkInactiveNameExists(inactiveNameInput.trim());
    setCheckingName(false);

    if (exists) {
      toast.error('This inactive name is already in use. Please choose a different name.');
      return;
    }

    // Proceed with deactivation
    await performDeactivation(userToDeactivate.id, inactiveNameInput.trim());
    
    // Close modal
    setShowInactiveNameModal(false);
    setUserToDeactivate(null);
    setInactiveNameInput('');
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeUsers = filteredUsers.filter(u => u.is_active);
  const inactiveUsers = filteredUsers.filter(u => !u.is_active);

  // Check if current user is admin
  if (currentUser && currentUser.role !== 'admin') {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="glass-card p-8 text-center">
          <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-400">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2 flex items-center gap-3">
          <Users className="text-white" />
          User Management
        </h1>
        <p className="text-sm sm:text-base text-gray-400">Manage user accounts and access</p>
      </div>

      {/* Search Bar */}
      <div className="glass-card p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-white transition-colors"
          />
        </div>
      </div>

      {loading ? (
        <div className="glass-card p-8 text-center">
          <div className="loading-dots mb-4">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p className="text-gray-400">Loading users...</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Active Users */}
          <div className="glass-card p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <UserCheck className="text-green-400" size={20} />
              <h2 className="text-lg sm:text-xl font-semibold">Active Users ({activeUsers.length})</h2>
            </div>
            <div className="space-y-2">
              {activeUsers.length === 0 ? (
                <p className="text-gray-400 text-sm">No active users found</p>
              ) : (
                activeUsers.map(user => (
                  <div
                    key={user.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 sm:p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <div className="flex items-start sm:items-center gap-3 sm:gap-4 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white font-semibold flex-shrink-0">
                        {user.name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-white break-words">{user.name}</p>
                          {user.role === 'admin' && (
                            <span className="px-2 py-0.5 bg-white/10 text-white text-xs font-medium rounded">
                              Admin
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-400 break-all">{user.email}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleUserStatus(user.id, user.is_active)}
                      disabled={updating === user.id || user.id === currentUser?.id}
                      className={`w-full sm:w-auto px-4 py-2 rounded-lg font-medium transition-colors ${
                        user.id === currentUser?.id
                          ? 'bg-gray-600/20 text-gray-500 cursor-not-allowed'
                          : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                      }`}
                    >
                      {updating === user.id ? 'Updating...' : user.id === currentUser?.id ? 'Current User' : 'Deactivate'}
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Inactive Users */}
          <div className="glass-card p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <UserX className="text-red-400" size={20} />
              <h2 className="text-lg sm:text-xl font-semibold">Inactive Users ({inactiveUsers.length})</h2>
            </div>
            <div className="space-y-2">
              {inactiveUsers.length === 0 ? (
                <p className="text-gray-400 text-sm">No inactive users</p>
              ) : (
                inactiveUsers.map(user => (
                  <div
                    key={user.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 sm:p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors opacity-60"
                  >
                    <div className="flex items-start sm:items-center gap-3 sm:gap-4 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-500 font-semibold border border-white/5 flex-shrink-0">
                        {user.name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-400 break-words">{user.name}</p>
                          <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs font-medium rounded">
                            Inactive
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 break-all">{user.email}</p>
                        {user.inactive_user_name && (
                          <p className="text-xs text-gray-600 mt-1">
                            Display as: <span className="text-orange-400 font-medium">{user.inactive_user_name}</span>
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => toggleUserStatus(user.id, user.is_active)}
                      disabled={updating === user.id}
                      className="w-full sm:w-auto px-4 py-2 bg-green-500/20 text-green-400 rounded-lg font-medium hover:bg-green-500/30 transition-colors"
                    >
                      {updating === user.id ? 'Updating...' : 'Activate'}
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Inactive Name Modal */}
      {showInactiveNameModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="glass-card rounded-xl p-4 sm:p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">Set Inactive Display Name</h3>
              <button
                onClick={() => {
                  setShowInactiveNameModal(false);
                  setInactiveNameInput('');
                  setUserToDeactivate(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-400 text-sm mb-4">
              Enter a display name for this inactive user (e.g., "user_1", "ex_employee_1")
            </p>

            <input
              type="text"
              value={inactiveNameInput}
              onChange={(e) => setInactiveNameInput(e.target.value)}
              placeholder="Enter inactive display name"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 mb-4"
              autoFocus
            />

            {checkingName && (
              <p className="text-yellow-400 text-sm mb-4">Checking name availability...</p>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  setShowInactiveNameModal(false);
                  setInactiveNameInput('');
                  setUserToDeactivate(null);
                }}
                className="flex-1 px-4 py-2 bg-white/5 text-white rounded-lg font-medium hover:bg-white/10 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleInactiveNameSubmit}
                disabled={!inactiveNameInput.trim() || checkingName}
                className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
